// src/context/useInVenueUsers.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "../api/axiosInstance";
import { alCambiarSaldo } from "../lib/saldoBus";

const InVenueUsersContext = createContext(null);

function normalizeVenueUser(u) {
  const imagePath = u.profile?.image_path || u.profile?.documents_path || "";
  return {
    ...u,
    entry_time: u.entry_time ? new Date(u.entry_time) : null,
    profile: {
      ...u.profile,
      image_path: imagePath,
    },
  };
}

function applyCreditPatch(users, userId, balance) {
  if (userId == null || balance === undefined) return users;
  const bal = Number(balance);
  if (!Number.isFinite(bal)) return users;
  return users.map((u) => {
    if (String(u.id) !== String(userId)) return u;
    return {
      ...u,
      profile: {
        ...u.profile,
        credit_balance: bal,
        cash_balance: bal,
      },
    };
  });
}

export function InVenueUsersProvider({ token, venueID, children }) {
  const [usersInVenue, setUsersInVenue] = useState([]);
  // false hasta que /v1/active-entries contesta por primera vez (o falla). Una
  // lista vacía sin `cargada` es «aún no sé», no «no hay nadie»: pintarla como
  // «Fuera de sala» era el rojo que veía Kong al entrar en Retirada (20-09-2026).
  const [cargada, setCargada] = useState(false);

  const markUserEntered = useCallback((user) => {
    const normalizedUser = normalizeVenueUser(user);
    setUsersInVenue((prev) => {
      const filtered = prev.filter((u) => u.id !== normalizedUser.id);
      return [normalizedUser, ...filtered];
    });
  }, []);

  const markUserLeft = useCallback((userId) => {
    setUsersInVenue((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const updateUserCredit = useCallback((userId, newBalance) => {
    setUsersInVenue((prev) => applyCreditPatch(prev, userId, newBalance));
  }, []);

  // Bus de saldo (plan CRUO §E): quien cobra emite, esta caché escucha y se
  // parchea — así CUALQUIER pantalla que pinte la lista de sala se entera al
  // momento, aunque el cobro ocurra en otro componente o en otra pestaña.
  // Escuchar (sin re-emitir) no puede formar bucle.
  useEffect(() => {
    return alCambiarSaldo(({ userId, newBalance } = {}) => {
      if (userId == null) return;
      setUsersInVenue((prev) => applyCreditPatch(prev, userId, newBalance));
    });
  }, []);

  const updateUserImage = useCallback((userId, newImagePath) => {
    setUsersInVenue((prev) =>
      prev.map((u) => {
        if (u.id !== userId) return u;
        return {
          ...u,
          profile: {
            ...u.profile,
            image_path: newImagePath,
          },
        };
      })
    );
  }, []);

  const updateUser = useCallback((userId, updatedUserData) => {
    setUsersInVenue((prev) =>
      prev.map((u) => {
        if (u.id !== userId) return u;
        const updated = {
          ...u,
          ...updatedUserData,
          profile: {
            ...u.profile,
            ...updatedUserData.profile,
          },
        };
        if (updatedUserData.profile?.documents_path !== undefined) {
          updated.profile.image_path = updatedUserData.profile.documents_path || null;
        }
        return updated;
      })
    );
  }, []);

  const sortUsers = useCallback((field, ascending = true) => {
    setUsersInVenue((prev) =>
      [...prev].sort((a, b) => {
        const getNestedValue = (obj, path) =>
          path.split(".").reduce((current, prop) => (current && current[prop] !== undefined ? current[prop] : null), obj);

        let av;
        let bv;

        if (field === "email") {
          av = a.email ?? "";
          bv = b.email ?? "";
        } else if (field.includes(".")) {
          av = getNestedValue(a, field) ?? 0;
          bv = getNestedValue(b, field) ?? 0;
        } else {
          av = a[field] ?? 0;
          bv = b[field] ?? 0;
        }

        if (typeof av === "number" && typeof bv === "number") {
          return ascending ? av - bv : bv - av;
        }

        const aStr = String(av).toLowerCase();
        const bStr = String(bv).toLowerCase();
        if (aStr < bStr) return ascending ? -1 : 1;
        if (aStr > bStr) return ascending ? 1 : -1;
        return 0;
      })
    );
  }, []);

  const refresh = useCallback(async (opts = {}) => {
    if (!token || !venueID) return;
    const creditPatch = opts?.creditPatch;
    try {
      const res = await axios.get("/v1/active-entries", {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });

      let normalized = (res.data || []).map(normalizeVenueUser);
      if (creditPatch?.userId != null && creditPatch.balance !== undefined) {
        normalized = applyCreditPatch(normalized, creditPatch.userId, creditPatch.balance);
      }
      setUsersInVenue(normalized);
    } catch (err) {
      console.error("Failed to refresh in-venue users", err);
    } finally {
      // También si falla: mejor «Fuera de sala» que un «Comprobando…» eterno.
      setCargada(true);
    }
  }, [token, venueID]);

  // Solo al cambiar de club (o de sesión) se vacía y se vuelve a pedir. Entre
  // pantallas la lista se conserva: el provider vive FUERA del <div key> de
  // Layout, ver el comentario allí.
  useEffect(() => {
    setUsersInVenue([]);
    setCargada(false);
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      usersInVenue,
      cargada,
      markUserEntered,
      markUserLeft,
      sortUsers,
      refresh,
      updateUserCredit,
      updateUserImage,
      updateUser,
    }),
    [usersInVenue, cargada, markUserEntered, markUserLeft, sortUsers, refresh, updateUserCredit, updateUserImage, updateUser]
  );

  return <InVenueUsersContext.Provider value={value}>{children}</InVenueUsersContext.Provider>;
}

export function useInVenueUsers(_token, _venueID) {
  const ctx = useContext(InVenueUsersContext);
  if (!ctx) {
    throw new Error("useInVenueUsers must be used within InVenueUsersProvider");
  }
  return ctx;
}
